import { useCallback, useEffect, useRef, useState } from 'react';

export function useScrollToBottom() {
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const isUserScrollingRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  const checkIfAtBottom = useCallback(() => {
    if (!containerRef.current) {
      return true;
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 100;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (!containerRef.current) {
      return;
    }

    // Mark as programmatic scroll so the scroll handler doesn't treat it as user scroll
    isProgrammaticScrollRef.current = true;

    // Immediately mark as at bottom so auto-scroll continues to work
    // This is critical when user scrolled up, sends a message, and we want
    // auto-scroll to stick while streaming
    setIsAtBottom(true);
    isAtBottomRef.current = true;

    containerRef.current.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const scrollToElement = useCallback((element: HTMLElement, behavior: ScrollBehavior = 'instant') => {
    isProgrammaticScrollRef.current = true;
    setIsAtBottom(false);
    isAtBottomRef.current = false;
    element.scrollIntoView({ behavior, block: 'center' });
  }, []);

  // Handle user scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      // Don't interfere with programmatic scrolls - keep isAtBottom true
      // and don't mark as user scrolling
      if (isProgrammaticScrollRef.current) {
        clearTimeout(scrollTimeout);

        // Reset programmatic scroll flag once we reach the bottom
        const atBottom = checkIfAtBottom();
        if (atBottom) {
          isProgrammaticScrollRef.current = false;
        }

        // Use timeout as fallback to reset flag (smooth scroll may not reach exact bottom)
        scrollTimeout = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 300);
        return;
      }

      // Mark as user scrolling
      isUserScrollingRef.current = true;
      clearTimeout(scrollTimeout);

      // Update isAtBottom state
      const atBottom = checkIfAtBottom();
      setIsAtBottom(atBottom);
      isAtBottomRef.current = atBottom;

      // Reset user scrolling flag after scroll ends
      scrollTimeout = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [checkIfAtBottom]);

  // Auto-scroll when content changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const scrollIfNeeded = () => {
      // Only auto-scroll if user was at bottom and isn't actively scrolling
      if (isAtBottomRef.current && !isUserScrollingRef.current) {
        // Mark as programmatic scroll so the scroll handler doesn't treat it as user scroll
        isProgrammaticScrollRef.current = true;

        requestAnimationFrame(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'instant',
          });
          setIsAtBottom(true);
          isAtBottomRef.current = true;

          // Reset programmatic flag after a short delay to ensure scroll events have fired
          requestAnimationFrame(() => {
            isProgrammaticScrollRef.current = false;
          });
        });
      }
    };

    // Watch for DOM changes
    const mutationObserver = new MutationObserver(scrollIfNeeded);
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Watch for size changes
    const resizeObserver = new ResizeObserver(scrollIfNeeded);
    resizeObserver.observe(container);

    // Also observe children for size changes
    Array.from(container.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  function onViewportEnter() {
    setIsAtBottom(true);
    isAtBottomRef.current = true;
  }

  function onViewportLeave() {
    setIsAtBottom(false);
    isAtBottomRef.current = false;
  }

  return {
    containerRef,
    endRef,
    isAtBottom,
    scrollToBottom,
    scrollToElement,
    onViewportEnter,
    onViewportLeave,
  };
}
